<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click="$emit('close')">
    <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4" @click.stop>
      <h2 class="text-xl font-bold mb-4">Add Tasks to Campaign</h2>

      <div class="mb-4">
        <p class="text-sm text-gray-600 mb-2">
          {{ selectedTaskCount }} task{{ selectedTaskCount !== 1 ? 's' : '' }} selected
        </p>
      </div>

      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Select Campaign</label>
        <select
          v-model="selectedCampaignId"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
        >
          <option :value="null">Select a campaign...</option>
          <option v-for="campaign in availableCampaigns" :key="campaign.id" :value="campaign.id">
            {{ campaign.name }} - {{ campaign.status }} ({{ campaign.total_tasks }} tasks)
          </option>
        </select>

        <p v-if="availableCampaigns.length === 0" class="mt-2 text-sm text-gray-500">
          No campaigns available. Create one in the Outreach section first.
        </p>
      </div>

      <div class="flex gap-2">
        <button
          @click="handleAddToCampaign"
          :disabled="!selectedCampaignId || isProcessing"
          class="btn-primary flex-1"
        >
          {{ isProcessing ? 'Adding...' : 'Add to Campaign' }}
        </button>
        <button @click="$emit('close')" class="btn-secondary flex-1" :disabled="isProcessing">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import { useToast } from 'vue-toastification'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  selectedTasks: {
    type: Array,
    required: true,
    default: () => [],
  },
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()

const selectedCampaignId = ref(null)
const availableCampaigns = ref([])
const isProcessing = ref(false)

const selectedTaskCount = computed(() => props.selectedTasks.length)

watch(() => props.isOpen, async (newVal) => {
  if (newVal) {
    selectedCampaignId.value = null
    await fetchCampaigns()
  }
})

const fetchCampaigns = async () => {
  try {
    const response = await axios.get('api/outreach/campaigns/', {
      params: {
        status__in: 'draft,active,paused',
      }
    })
    availableCampaigns.value = response.data
  } catch (error) {
    console.error('Failed to fetch campaigns:', error)
    toast.error('Failed to load campaigns')
  }
}

const handleAddToCampaign = async () => {
  if (!selectedCampaignId.value) return

  isProcessing.value = true

  try {
    const task_ids = props.selectedTasks.map(task => task.id)

    const response = await axios.post(
      `api/outreach/campaigns/${selectedCampaignId.value}/add_tasks/`,
      { task_ids }
    )

    console.log('Campaign response:', response.data)

    if (response.data.successful > 0) {
      toast.success(`Added ${response.data.successful} task${response.data.successful !== 1 ? 's' : ''} to campaign`)
      emit('success')
      emit('close')
    } else {
      toast.error(`Failed to add tasks to campaign. ${response.data.failed || 0} failed.`)
    }

  } catch (error) {
    console.error('Failed to add tasks:', error)
    const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to add tasks to campaign'
    toast.error(errorMsg)
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
</style>
